import React, { useState } from "react";
import { IonButton, IonIcon, IonImg, IonLoading, IonModal, IonSkeletonText } from "@ionic/react";
import { Card, PaymentStatusType } from "@core/types";

import { toast } from "react-toastify";
import { Browser } from "@capacitor/browser";
import classNames from "classnames/bind";
import styles from "./Payment.module.scss";
import { cardOutline } from "ionicons/icons";
import { useTranslation } from "react-i18next";
import { PAYMENT_CARDS_LOGO } from "../../../../assets/logos";
import CommonModal from "../../../../components/UI/CommonModal/CommonModal";
import { useAuth } from "../../../../contexts/auth-context";
import { useGetCardsQuery } from "../../../../providers/cards";
import {
  useCreateYooKassaPaymentMutation,
  usePayWithSavedMethodMutation,
  useGetPaymentStatus,
} from "../../../../providers/payment";

interface PaymentProps {
  orderId: string;
  price: number;
  currencySymbol: string;
  onSuccess?: () => void;
  onError?: (error: any) => void;
}

const cx = classNames.bind(styles);

enum PaymentMethodState {
  NONE,
  SAVED_CARD,
  NEW_CARD,
}

const Payment: React.FC<PaymentProps> = ({ orderId, price, currencySymbol }) => {
  const { user } = useAuth();
  const userId = user?.id;
  const { t } = useTranslation();
  const [selectedPaymentCard, setSelectedPaymentCard] = useState<Card | null>(null);
  const [paymentMethodState, setPaymentMethodState] = useState<PaymentMethodState>(PaymentMethodState.NONE);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [paymentId, setPaymentId] = useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatusType | null>(null);

  const isLoading = paymentStatus === "pending" || isPaymentLoading;

  const { data: cards, isLoading: isCardsLoading } = useGetCardsQuery({
    onSuccess: (data) => {
      setSelectedPaymentCard(data[data.length - 1]);
      setPaymentMethodState(PaymentMethodState.SAVED_CARD);
    },
  });

  const { mutateAsync: createYooKassaPaymentAsync } = useCreateYooKassaPaymentMutation();
  const { mutateAsync: payWithSavedMethodMutationAsync } = usePayWithSavedMethodMutation();

  useGetPaymentStatus(paymentId, paymentStatus, orderId, setPaymentStatus);

  const handlePay = async () => {
    try {
      setIsPaymentLoading(true);
      setPaymentStatus("pending");

      if (!userId) {
        toast.warn(`${t("Login Required")}: ${t("Please log in to create an order.")}`);
        return;
      }

      if (!price || price <= 0) {
        toast.warn(`${t("Invalid Price")}: ${t("Invalid price. Please try again.")}`);
        return;
      }

      // Pay through saved (selected) card
      if (selectedPaymentCard && userId) {
        try {
          const { data: responseData }: any = await payWithSavedMethodMutationAsync({
            amount: {
              value: price.toString(),
              currency: "RUB",
            },
            payment_method_id: selectedPaymentCard.paymentMethodId,
            metadata: {
              profile_id: userId,
              order_id: orderId,
            },
          });

          console.log({ responseData });

          setPaymentId(responseData?.metadata?.payment_id);

          if (responseData?.confirmation?.confirmation_url) {
            const confirmationUrl = responseData.confirmation.confirmation_url;
            try {
              await Browser.open({ url: confirmationUrl });
            } catch (error) {
              console.error("Error opening web browser:", error);
              throw new Error("Failed to open payment confirmation page");
            }
          }
        } catch (error: any) {
          console.error(error);
          toast.error(`${t("Error processing payment")}: ${error?.message || "Unknown error"}`);
          setPaymentStatus("failed");
        } finally {
          setIsPaymentLoading(false);
          setShowPaymentModal(false);
        }
        return;
      }

      // Pay through registering new card
      try {
        const { data: createYooKassaPaymentResponse }: any = await createYooKassaPaymentAsync({
          amount: price,
          profileId: userId,
          orderId: orderId,
        });

        setPaymentId(createYooKassaPaymentResponse?.metadata?.payment_id);

        console.log("createYooKassaPaymentResponse", createYooKassaPaymentResponse);

        if (createYooKassaPaymentResponse?.confirmation?.confirmation_token) {
          const paymentUrl = `${process.env.REACT_APP_PUBLIC_PAYMENTS_URL}/?confirmationToken=${createYooKassaPaymentResponse?.confirmation?.confirmation_token}`;

          console.log("paymentUrl", paymentUrl);

          await Browser.open({ url: paymentUrl });
        }
      } catch (error: any) {
        console.error(error);
        toast.error(`${t("Error initiating payment")}: ${error?.message || "Unknown error"}`);
        setPaymentStatus("failed");
      } finally {
        setShowPaymentModal(false);
        setIsPaymentLoading(false);
      }
    } catch (error: any) {
      console.error("Error handling payment:", error);
      toast.error(`${t("Error processing payment")}: ${error?.message || "Unknown error"}`);
      setPaymentStatus("failed");
    }
  };

  const handlePaymentMethodChange = async (cardId: string) => {
    if (cardId === "ADD_NEW") {
      setSelectedPaymentCard(null);
      setPaymentMethodState(PaymentMethodState.NEW_CARD);
    } else {
      setSelectedPaymentCard(cards?.find((card) => card.id === cardId) || null);
      setPaymentMethodState(PaymentMethodState.SAVED_CARD);
    }
  };

  const handleClose = () => {
    setShowPaymentModal(false);
  };

  return (
    <>
      <IonButton expand="block" disabled={isLoading} onClick={() => setShowPaymentModal(true)}>
        {selectedPaymentCard && typeof selectedPaymentCard !== "string" ? (
          <div className="flex items-center gap-2">
            {selectedPaymentCard.cardType && PAYMENT_CARDS_LOGO[selectedPaymentCard.cardType.toLowerCase()] && (
              <IonImg
                src={PAYMENT_CARDS_LOGO[selectedPaymentCard.cardType.toLowerCase()]}
                alt={selectedPaymentCard.cardType}
                className="w-12 h-8 object-contain"
              />
            )}
            <span>**** **** **** {selectedPaymentCard.cardLastFour}</span>
          </div>
        ) : (
          t("Choose Payment")
        )}
      </IonButton>
      <IonModal isOpen={showPaymentModal} onWillDismiss={handleClose}>
        <CommonModal title={t("Choose Payment")} onDismiss={handleClose} classNameContent={cx("modalContent")}>
          <IonLoading isOpen={isLoading} message={t("Please wait...")} />
          <div className="gap-2 p-4 shadow-lg rounded-lg">
            <div className="text-center text-lg font-bold">
              {t("To pay")} {price.toLocaleString("ru-RU")} {currencySymbol}
            </div>

            <div className=" space-y-2">
              {isCardsLoading ? (
                <IonSkeletonText animated className="m-0 h-11" />
              ) : (
                cards?.map((card) => (
                  <IonButton
                    expand="block"
                    key={card.paymentMethodId}
                    disabled={isLoading}
                    className="flex items-center justify-between gap-4"
                    onClick={() => handlePaymentMethodChange(card.id)}
                    fill={selectedPaymentCard?.id === card.id ? "solid" : "outline"}
                  >
                    <div className="flex items-center gap-4">
                      {card.cardType && PAYMENT_CARDS_LOGO[card.cardType.toLowerCase()] && (
                        <IonImg
                          src={PAYMENT_CARDS_LOGO[card.cardType.toLowerCase()]}
                          alt={card.cardType}
                          className="w-12 h-8 object-contain"
                        />
                      )}
                      <span>**** **** **** {card.cardLastFour}</span>
                    </div>
                  </IonButton>
                ))
              )}
              <IonButton
                expand="block"
                disabled={isLoading}
                onClick={() => handlePaymentMethodChange("ADD_NEW")}
                fill={selectedPaymentCard === null ? "solid" : "outline"}
              >
                <div className="flex items-center gap-1">
                  <IonIcon icon={cardOutline} />
                  <span className="ml-2">{t("New Card")}</span>
                </div>
              </IonButton>
            </div>
          </div>
          <IonButton
            expand="block"
            onClick={handlePay}
            className="mb-2"
            disabled={paymentMethodState === PaymentMethodState.NONE}
          >
            {t("Pay Now")}
          </IonButton>
        </CommonModal>
      </IonModal>
    </>
  );
};

export default Payment;
